import type { TouchEvent } from 'react'
import type { GalleryPhoto, SectionRow } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Expand } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'

import Photo from '@/components/landing/photo'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

interface GallerySectionProps {
    section?: SectionRow
    photos: Array<GalleryPhoto>
}

const TILTS = ['-rotate-3', 'rotate-2', '-rotate-1', 'rotate-3', '-rotate-2', 'rotate-1', '-rotate-3', 'rotate-2']
const TAPES = ['bg-[#f7d9a8]/75', 'bg-[#f9c6d4]/80', 'bg-[#cfe3f2]/75', 'bg-[#f7d9a8]/75']

export default function GallerySection({ section, photos }: GallerySectionProps) {
    const [active, setActive] = useState<number | null>(null)
    const [lastShown, setLastShown] = useState(0)
    const touchStart = useRef<number | null>(null)
    const total = photos.length
    const open = active !== null
    const shownIndex = active ?? lastShown
    const current = photos[shownIndex]

    useEffect(() => {
        if (active !== null) setLastShown(active)
    }, [active])

    const step = (direction: number) => {
        setActive(index => index === null ? null : (index + direction + total) % total)
    }

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'ArrowRight') setActive(index => index === null ? null : (index + 1) % total)
            if (event.key === 'ArrowLeft') setActive(index => index === null ? null : (index - 1 + total) % total)
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open, total])

    const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
        touchStart.current = event.touches[0]?.clientX ?? null
    }

    const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
        const start = touchStart.current
        const end = event.changedTouches[0]?.clientX

        touchStart.current = null

        if (start === null || end === undefined) return
        if (Math.abs(end - start) < 45) return

        step(end < start ? 1 : -1)
    }

    return (
        <section id='gallery' className='relative overflow-hidden bg-gradient-to-b from-blush via-[#fff1f4] to-cream py-24 sm:py-32'>
            <div className='relative mx-auto max-w-6xl px-6 lg:px-10'>
                <SectionHeading section={section} />

                <ul className='mt-16 flex flex-wrap justify-center gap-x-5 gap-y-12 sm:mt-20 sm:gap-x-8'>
                    {photos.map((photo, index) => (
                        <li key={photo.id} className='w-[calc(50%-0.625rem)] sm:w-[calc(33.333%-1.4rem)] lg:w-[calc(25%-1.5rem)]'>
                            <Reveal direction='zoom' delay={(index % 4) * 90}>
                                <button
                                    type='button'
                                    onClick={() => setActive(index)}
                                    className={cn(
                                        'group relative block w-full rounded-[3px] bg-white p-2 pb-3 text-left shadow-[0_18px_40px_-22px_rgba(91,21,48,0.55)] transition duration-500 outline-none hover:z-10 hover:scale-[1.04] hover:rotate-0 focus-visible:z-10 focus-visible:rotate-0 focus-visible:ring-4 focus-visible:ring-petal/40 sm:p-3 sm:pb-4',
                                        TILTS[index % TILTS.length]
                                    )}
                                >
                                    <span aria-hidden className={cn('absolute -top-3 left-1/2 h-6 w-20 -translate-x-1/2 -rotate-3 shadow-sm', TAPES[index % TAPES.length])} />

                                    <span className='relative block'>
                                        <Photo
                                            as='span'
                                            src={photo.photo ?? ''}
                                            alt={photo.photo_alt ?? ''}
                                            rotation={photo.photo_rotation}
                                            className='aspect-square bg-blush'
                                            imageClassName='transition-transform duration-700 group-hover:scale-105'
                                        />

                                        <span aria-hidden className='absolute right-2 bottom-2 grid size-8 place-items-center rounded-full bg-white/85 text-wine opacity-0 shadow transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100'>
                                            <Expand className='size-4' />
                                        </span>
                                    </span>

                                    {photo.caption && (
                                        <span
                                            data-mdlfy-cms={`gallery_photos:${photo.id}:caption`}
                                            className='mt-3 block text-center font-hand text-lg leading-tight text-ink/85 sm:text-xl'
                                        >
                                            {photo.caption}
                                        </span>
                                    )}
                                </button>
                            </Reveal>
                        </li>
                    ))}
                </ul>
            </div>

            <Dialog open={open} onOpenChange={next => { if (!next) setActive(null) }}>
                <DialogContent
                    className='w-[min(94vw,980px)] max-w-none gap-0 rounded-2xl bg-[#fffdf8] p-3 sm:max-w-none sm:p-4'
                    overlayClassName='bg-night/80 supports-backdrop-filter:backdrop-blur-sm'
                    closeClassName='top-4 right-4 z-10 bg-white/90 text-wine shadow hover:bg-white'
                >
                    {current && (
                        <>
                            <DialogTitle className='sr-only'>
                                Photo {shownIndex + 1} / {total}
                            </DialogTitle>

                            <div
                                className='relative h-[min(66vh,700px)] w-full overflow-hidden rounded-xl bg-[#f6ece6]'
                                onTouchStart={onTouchStart}
                                onTouchEnd={onTouchEnd}
                            >
                                <Photo
                                    key={current.id}
                                    src={current.photo ?? ''}
                                    alt={current.photo_alt ?? ''}
                                    rotation={current.photo_rotation}
                                    fit='contain'
                                    eager
                                    className='absolute inset-0 animate-in fade-in duration-300'
                                />
                            </div>

                            <div className='flex items-center gap-3 pt-4'>
                                <button
                                    type='button'
                                    onClick={() => step(-1)}
                                    aria-label='Pichli photo'
                                    className='grid size-11 shrink-0 place-items-center rounded-full bg-blush text-wine transition outline-none hover:bg-petal hover:text-white focus-visible:ring-4 focus-visible:ring-petal/40'
                                >
                                    <ChevronLeft aria-hidden className='size-5' />
                                </button>

                                <div className='min-w-0 flex-1 text-center'>
                                    {current.caption && (
                                        <p
                                            data-mdlfy-cms={`gallery_photos:${current.id}:caption`}
                                            className='font-hand text-2xl leading-tight text-ink sm:text-3xl'
                                        >
                                            {current.caption}
                                        </p>
                                    )}

                                    <p className='mt-1 text-xs font-semibold tracking-[0.2em] text-muted-foreground'>
                                        {shownIndex + 1} / {total}
                                    </p>
                                </div>

                                <button
                                    type='button'
                                    onClick={() => step(1)}
                                    aria-label='Agli photo'
                                    className='grid size-11 shrink-0 place-items-center rounded-full bg-blush text-wine transition outline-none hover:bg-petal hover:text-white focus-visible:ring-4 focus-visible:ring-petal/40'
                                >
                                    <ChevronRight aria-hidden className='size-5' />
                                </button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    )
}
